import { feetToMeters, metersToFeet, type UnitSystem } from '@/core/Units';
import type { Heightfield } from './TerrainTypes';

/**
 * Contour extraction: a heightfield read the way a surveyor reads it (spec §6.4).
 *
 * ## Why isolines rather than a colour ramp
 *
 * A false-colour elevation ramp is far cheaper — one shader, no geometry — and it is the wrong
 * answer for this app. A ramp tells you *which end of the site is higher*; a contour tells you
 * **where the slope is steep**, because that is what the spacing between two lines means. Grading a
 * bank, siting a path, deciding whether a bed drains: every one of those is a question about spacing,
 * and only a drawn line answers it. The classical map convention is not decoration here, it is the
 * data structure that happens to be legible.
 *
 * ## Why marching squares, and why the output is chained
 *
 * Marching squares over the height lattice is the standard tracing, and it falls out of the grid the
 * terrain already is. But the raw output is a heap of disconnected two-point segments, which is
 * enough to *draw* a contour and not enough to *label* one — a label has to be placed along a run of
 * line, spaced by distance travelled, and turned to sit with it. So segments are chained back into
 * polylines here rather than in the view. The chaining is exact rather than tolerance-based: every
 * crossing lies on a known lattice edge, so two segments meet when they name the same edge, and no
 * floating-point comparison is involved.
 *
 * This module is pure and knows nothing about Three.js — see `@/editor/ContourOverlay` for the view.
 */

/** A single traced contour at one elevation. */
export interface ContourLine {
  /** Height above the site datum, in metres. */
  readonly elevationMeters: number;
  /**
   * Every fifth contour, counted from the datum. Drawn heavier, and the only one that carries a
   * number — the USGS convention, and the reason a topo map reads as a hierarchy rather than a
   * hatch: you count four thin lines between two labelled ones without reading anything.
   */
  readonly index: boolean;
  /** Terrain-local positions, flat `[x0, z0, x1, z1, …]` in metres. */
  readonly points: Float32Array;
  /** True when the line closes on itself — a summit, a hollow, or a ring around a berm. */
  readonly closed: boolean;
  /** Length along the line in metres, for label spacing. */
  readonly lengthMeters: number;
}

export interface ContourSet {
  /** Vertical distance between adjacent lines, in metres. */
  readonly intervalMeters: number;
  /** Vertical distance between the heavier, numbered lines. A whole multiple of the interval. */
  readonly indexIntervalMeters: number;
  readonly minHeightMeters: number;
  readonly maxHeightMeters: number;
  readonly lines: readonly ContourLine[];
  /**
   * Whether the range spans enough contours for the index ones to form a readable skeleton. On very
   * flat ground it does not, and the view labels everything instead — a map with no numbers at all
   * is worse than one that numbers every line.
   */
  readonly hasIndexLines: boolean;
}

export interface ContourOptions {
  /** Vertical spacing. Omitted means {@link chooseContourInterval} picks one for the range. */
  intervalMeters?: number;
  /** Which unit's round numbers the automatic interval should land on. */
  unit?: UnitSystem;
  /**
   * Lines shorter than this are dropped. Sculpting leaves millimetre ripples that trace as confetti
   * around the real lines; a map speckled with three-centimetre squiggles reads as noise, and none of
   * them are a slope anybody is going to grade.
   */
  minLengthMeters?: number;
}

/**
 * How many lines apart the index contours run.
 *
 * Every fifth is the survey convention, and it assumes a map with dozens of lines on it. A garden
 * has ten. Every fifth of ten leaves two heavy lines, one of which is usually the datum sitting at
 * the site's edge — and because the index lines are the labelled ones, the result is a map with the
 * same number written on it fifteen times. So the step is chosen to land three or four numbered
 * levels across whatever relief there is, which is the same idea at the scale it is drawn at, and it
 * settles back onto every fifth as soon as the ground is big enough to deserve it.
 *
 * Returning 1 means "no hierarchy": too few lines to have a skeleton at all, and the caller marks
 * none of them as index, which is the signal to the view that every line should carry its number.
 */
function chooseIndexEvery(levelCount: number): number {
  const step = Math.round(levelCount / TARGET_INDEX_LINES);
  return Math.min(5, Math.max(1, Number.isFinite(step) ? step : 1));
}

/** Numbered lines we aim for. Enough to read a slope between, few enough that each still stands out. */
const TARGET_INDEX_LINES = 3.5;

/**
 * Nudge applied to every level before comparing against a vertex height.
 *
 * A level that lands exactly on a lattice vertex is the one degenerate case marching squares has:
 * the vertex is neither above nor below, and the two cells sharing it can disagree, which tears the
 * line. A micrometre of bias makes "on the level" mean "below" everywhere at once. It is far below
 * anything the ground records and far above float noise at these magnitudes.
 */
const LEVEL_EPSILON = 1e-6;

/**
 * Round vertical steps, in metres. The 1–2–5 ladder every chart has used since paper.
 *
 * Reaches down to a centimetre, far below anything a survey map would print, because a garden is
 * where that matters: the whole question about a lawn is which way two centimetres of fall runs, and
 * a map whose finest line is half a metre answers it with a blank sheet.
 */
const METRIC_STEPS: readonly number[] = [0.01, 0.02, 0.05, 0.1, 0.25, 0.5, 1, 2, 5, 10, 25, 50];

/**
 * The same ladder in feet — a site drawn in feet wants contours at whole feet, not at 0.3048 m.
 *
 * Stops at three inches rather than following the metric ladder all the way down: below that the
 * figures turn into two-decimal fractions of a foot, which is a number nobody grades to.
 */
const IMPERIAL_STEPS: readonly number[] = [0.25, 0.5, 1, 2, 5, 10, 25, 50].map(feetToMeters);

/**
 * Contours we aim to draw across the site's full relief.
 *
 * Roughly a dozen is the density a garden-scale plan can carry: enough that a bank reads as a band
 * of close lines, few enough that a lawn does not fill with them.
 */
const TARGET_LINES = 12;

/** Hard ceiling on traced levels, so a freak amplitude cannot turn one rebuild into a stall. */
const MAX_LEVELS = 64;

/**
 * Pick a round vertical interval for a given relief.
 *
 * Rounds *up* to the ladder step rather than to the nearest: erring coarse gives a map with a few
 * clean lines, erring fine gives one that is mostly ink. The former is still readable.
 */
export function chooseContourInterval(rangeMeters: number, unit: UnitSystem = 'metric'): number {
  const steps = unit === 'imperial' ? IMPERIAL_STEPS : METRIC_STEPS;
  const smallest = steps[0] ?? 0.05;
  const largest = steps[steps.length - 1] ?? 50;
  if (!Number.isFinite(rangeMeters) || rangeMeters <= 0) return smallest;

  const ideal = rangeMeters / TARGET_LINES;
  for (const step of steps) {
    if (step >= ideal) return step;
  }
  return largest;
}

/**
 * Trace the heightfield into contour polylines.
 *
 * Cost is one pass over the lattice cells; a cell only visits the levels its own four corners
 * straddle, so flat ground costs almost nothing and the expense follows the relief. That is what
 * makes it affordable to re-trace between sculpt strokes.
 */
export function traceContours(field: Heightfield, options: ContourOptions = {}): ContourSet {
  const { cols, rows, heights } = field;
  const { min, max } = extent(heights);
  const range = max - min;
  const interval = options.intervalMeters ?? chooseContourInterval(range, options.unit ?? 'metric');
  const minLength = options.minLengthMeters ?? 0.6;

  const empty: ContourSet = {
    intervalMeters: interval,
    indexIntervalMeters: interval * 5,
    minHeightMeters: min,
    maxHeightMeters: max,
    lines: [],
    hasIndexLines: false,
  };
  if (cols < 2 || rows < 2 || range <= interval * 0.05) return empty;

  const kMin = Math.ceil((min - LEVEL_EPSILON) / interval);
  const kMax = Math.floor((max - LEVEL_EPSILON) / interval);
  if (kMax < kMin) return empty;
  // Clamp from the bottom up rather than the top down, so a runaway amplitude loses its summit
  // rather than its base — the ground you are standing on is the part worth drawing.
  const kLast = Math.min(kMax, kMin + MAX_LEVELS - 1);

  const dx = field.widthMeters / (cols - 1);
  const dz = field.lengthMeters / (rows - 1);
  const originX = -field.widthMeters / 2;
  const originZ = -field.lengthMeters / 2;

  const builds = new Map<number, LevelBuild>();
  const buildFor = (k: number): LevelBuild => {
    let build = builds.get(k);
    if (!build) {
      build = { edgeToPoint: new Map(), xs: [], zs: [], segA: [], segB: [] };
      builds.set(k, build);
    }
    return build;
  };

  for (let r = 0; r < rows - 1; r += 1) {
    const rowTop = r * cols;
    const rowBottom = (r + 1) * cols;
    for (let c = 0; c < cols - 1; c += 1) {
      // Corners, named by where they sit when the site is read from above: -z is the top of the page.
      const tl = heights[rowTop + c] ?? 0;
      const tr = heights[rowTop + c + 1] ?? 0;
      const bl = heights[rowBottom + c] ?? 0;
      const br = heights[rowBottom + c + 1] ?? 0;
      const lo = Math.min(tl, tr, bl, br);
      const hi = Math.max(tl, tr, bl, br);
      if (hi - lo <= 0) continue;

      const from = Math.max(kMin, Math.ceil((lo - LEVEL_EPSILON) / interval));
      const to = Math.min(kLast, Math.floor((hi - LEVEL_EPSILON) / interval));
      for (let k = from; k <= to; k += 1) {
        const level = k * interval + LEVEL_EPSILON;
        const code =
          (tl > level ? 1 : 0) | (tr > level ? 2 : 0) | (br > level ? 4 : 0) | (bl > level ? 8 : 0);
        if (code === 0 || code === 15) continue;

        const build = buildFor(k);
        const cellX = originX + c * dx;
        const cellZ = originZ + r * dz;
        // Each crossing is named by the lattice edge it lies on, so the cell next door finds the
        // very same point rather than a near-duplicate — this is what makes chaining exact.
        const top = (): number =>
          pointOn(build, 2 * (rowTop + c), cellX + crossing(tl, tr, level) * dx, cellZ);
        const bottom = (): number =>
          pointOn(build, 2 * (rowBottom + c), cellX + crossing(bl, br, level) * dx, cellZ + dz);
        const left = (): number =>
          pointOn(build, 2 * (rowTop + c) + 1, cellX, cellZ + crossing(tl, bl, level) * dz);
        const right = (): number =>
          pointOn(build, 2 * (rowTop + c + 1) + 1, cellX + dx, cellZ + crossing(tr, br, level) * dz);

        switch (code) {
          case 1:
          case 14:
            addSegment(build, top(), left());
            break;
          case 2:
          case 13:
            addSegment(build, top(), right());
            break;
          case 3:
          case 12:
            addSegment(build, left(), right());
            break;
          case 4:
          case 11:
            addSegment(build, right(), bottom());
            break;
          case 6:
          case 9:
            addSegment(build, top(), bottom());
            break;
          case 7:
          case 8:
            addSegment(build, left(), bottom());
            break;
          // The two saddles: opposite corners above the level, and the cell alone cannot say whether
          // they join through the middle or pinch apart. The average of the four corners stands in
          // for the centre height and decides — the standard resolution, and the one that keeps a
          // ridge continuous instead of breaking it into dashes at every pass.
          case 5: {
            const joined = (tl + tr + bl + br) / 4 > level;
            if (joined) {
              addSegment(build, top(), right());
              addSegment(build, left(), bottom());
            } else {
              addSegment(build, top(), left());
              addSegment(build, right(), bottom());
            }
            break;
          }
          case 10: {
            const joined = (tl + tr + bl + br) / 4 > level;
            if (joined) {
              addSegment(build, top(), left());
              addSegment(build, right(), bottom());
            } else {
              addSegment(build, top(), right());
              addSegment(build, left(), bottom());
            }
            break;
          }
          default:
            break;
        }
      }
    }
  }

  const lines: ContourLine[] = [];
  const levels = [...builds.keys()].sort((a, b) => a - b);
  const indexEvery = chooseIndexEvery(kLast - kMin + 1);
  let indexLevels = 0;
  for (const k of levels) {
    const build = builds.get(k);
    if (!build) continue;
    const index = indexEvery > 1 && k % indexEvery === 0;
    let kept = 0;
    for (const line of chain(build, k * interval, index)) {
      if (line.lengthMeters < minLength) continue;
      lines.push(line);
      kept += 1;
    }
    if (index && kept > 0) indexLevels += 1;
  }

  return {
    intervalMeters: interval,
    indexIntervalMeters: interval * indexEvery,
    minHeightMeters: min,
    maxHeightMeters: max,
    lines,
    hasIndexLines: indexLevels >= 2,
  };
}

/* --------------------------------- Internals --------------------------------- */

interface LevelBuild {
  /** Lattice edge id → index into `xs`/`zs`. The identity that makes chaining exact. */
  edgeToPoint: Map<number, number>;
  xs: number[];
  zs: number[];
  segA: number[];
  segB: number[];
}

function extent(heights: Float32Array): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < heights.length; i += 1) {
    const h = heights[i] ?? 0;
    if (h < min) min = h;
    if (h > max) max = h;
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return { min: 0, max: 0 };
  return { min, max };
}

/**
 * Where between two corner heights the level falls, as a fraction in [0, 1].
 *
 * The guard is for corners at equal height: the caller only asks when the level is between them, so
 * an equal pair means the level sits on both, and the midpoint is the only unbiased answer.
 */
function crossing(from: number, to: number, level: number): number {
  const span = to - from;
  if (Math.abs(span) < 1e-9) return 0.5;
  const t = (level - from) / span;
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

function pointOn(build: LevelBuild, edgeId: number, x: number, z: number): number {
  const existing = build.edgeToPoint.get(edgeId);
  if (existing !== undefined) return existing;
  const id = build.xs.length;
  build.xs.push(x);
  build.zs.push(z);
  build.edgeToPoint.set(edgeId, id);
  return id;
}

function addSegment(build: LevelBuild, a: number, b: number): void {
  if (a === b) return;
  build.segA.push(a);
  build.segB.push(b);
}

/**
 * Walk one level's segments into polylines.
 *
 * Open runs are walked first, from their loose ends: starting anywhere on an open run would cut it
 * in two and produce a pair of lines where the ground has one. Whatever segments remain after that
 * belong to closed rings, which have no ends and can be started anywhere.
 */
function chain(build: LevelBuild, elevation: number, index: boolean): ContourLine[] {
  const count = build.segA.length;
  if (count === 0) return [];

  const atPoint = new Map<number, number[]>();
  const attach = (point: number, segment: number): void => {
    const list = atPoint.get(point);
    if (list) list.push(segment);
    else atPoint.set(point, [segment]);
  };
  for (let i = 0; i < count; i += 1) {
    attach(build.segA[i] ?? 0, i);
    attach(build.segB[i] ?? 0, i);
  }

  const used = new Uint8Array(count);
  const lines: ContourLine[] = [];

  const walk = (start: number): void => {
    const chainPoints: number[] = [start];
    let node = start;
    for (;;) {
      const candidates = atPoint.get(node);
      if (!candidates) break;
      let next = -1;
      for (const segment of candidates) {
        if (!used[segment]) {
          next = segment;
          break;
        }
      }
      if (next < 0) break;
      used[next] = 1;
      const a = build.segA[next] ?? 0;
      node = a === node ? (build.segB[next] ?? 0) : a;
      chainPoints.push(node);
    }
    if (chainPoints.length < 2) return;
    lines.push(materialize(build, chainPoints, elevation, index));
  };

  for (const [point, segments] of atPoint) {
    if (segments.length === 1) walk(point);
  }
  for (let i = 0; i < count; i += 1) {
    if (!used[i]) walk(build.segA[i] ?? 0);
  }
  return lines;
}

function materialize(
  build: LevelBuild,
  chainPoints: readonly number[],
  elevation: number,
  index: boolean,
): ContourLine {
  const points = new Float32Array(chainPoints.length * 2);
  let length = 0;
  let previousX = 0;
  let previousZ = 0;
  for (let i = 0; i < chainPoints.length; i += 1) {
    const id = chainPoints[i] ?? 0;
    const x = build.xs[id] ?? 0;
    const z = build.zs[id] ?? 0;
    points[i * 2] = x;
    points[i * 2 + 1] = z;
    if (i > 0) length += Math.hypot(x - previousX, z - previousZ);
    previousX = x;
    previousZ = z;
  }
  return {
    elevationMeters: elevation,
    index,
    points,
    closed: chainPoints.length > 2 && chainPoints[0] === chainPoints[chainPoints.length - 1],
    lengthMeters: length,
  };
}

/**
 * A contour's elevation, written the way a map writes it.
 *
 * Precision follows the interval rather than a fixed number of places: at 0.25 m spacing "1.25 m"
 * is the number, and at 5 m spacing "10 m" is — a map that prints "10.00 m" is telling you about
 * its own arithmetic instead of about the ground.
 */
export function formatElevation(
  meters: number,
  unit: UnitSystem,
  intervalMeters: number,
): string {
  if (unit === 'imperial') {
    const feet = metersToFeet(meters);
    const step = metersToFeet(intervalMeters);
    return `${trim(feet, step)} ft`;
  }
  return `${trim(meters, intervalMeters)} m`;
}

function trim(value: number, step: number): string {
  const digits = step < 0.1 ? 2 : step < 1 ? 1 : 0;
  const text = value.toFixed(digits);
  // `-0.0` is arithmetic leaking into the drawing: the datum has one name.
  return Number.parseFloat(text) === 0 ? (0).toFixed(digits) : text;
}
